import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Button,
    Badge,
    Table,
    Progress,
    Spinner,
    Alert,
    Dropdown,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input,
    Form,
    FormGroup,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    Pagination,
    PaginationItem,
    PaginationLink
} from 'reactstrap';
import { supabase } from '../../lib/supabase';
import {
    FiPlus,
    FiFilter,
    FiSearch,
    FiDownload,
    FiMoreVertical,
    FiEdit,
    FiEye,
    FiTrash2,
    FiPause,
    FiPlay,
    FiCopy,
    FiArchive,
    FiTrendingUp,
    FiTrendingDown,
    FiDollarSign,
    FiUsers,
    FiTarget,
    FiGlobe,
    FiVideo,
    FiImage,
    FiSmartphone,
    FiHeadphones,
    FiRefreshCw,
    FiLink,
    FiCalendar,
    FiBarChart2,
    FiAlertCircle,
    FiCheckCircle,
    FiXCircle,
    FiPackage,
    FiFileText
} from 'react-icons/fi';
import { format, parseISO, differenceInDays } from 'date-fns';

const CampaignsPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [ads, setAds] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [error, setError] = useState(null);

    const itemsPerPage = 10;

    // Modals
    const [createModal, setCreateModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [linkAdModal, setLinkAdModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    // Form states
    const [campaignForm, setCampaignForm] = useState({
        name: '',
        description: '',
        campaign_type: 'display',
        objective: 'awareness',
        budget_type: 'daily',
        budget: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        status: 'draft',
        bidding_model: 'cpm',
        bid_amount: '',
        age_min: 18,
        age_max: 65,
        gender: 'all',
        locations: ['US'],
        interests: [],
        devices: ['mobile', 'desktop'],
        languages: ['en']
    });

    const [formErrors, setFormErrors] = useState({});

    // Campaign types
    const campaignTypes = [
        { id: 'display', name: 'Display/Banner', icon: <FiImage />, color: 'primary' },
        { id: 'native', name: 'Native Ads', icon: <FiGlobe />, color: 'info' },
        { id: 'video', name: 'Video Ads', icon: <FiVideo />, color: 'warning' },
        { id: 'audio', name: 'Audio Ads', icon: <FiHeadphones />, color: 'secondary' },
        { id: 'rich_media', name: 'Rich Media', icon: <FiTarget />, color: 'success' },
        { id: 'social', name: 'Social Media', icon: <FiUsers />, color: 'danger' },
        { id: 'search', name: 'Search Ads', icon: <FiSearch />, color: 'primary' },
        { id: 'mobile', name: 'Mobile Apps', icon: <FiSmartphone />, color: 'info' },
        { id: 'ctv', name: 'Connected TV', icon: <FiVideo />, color: 'warning' },
        { id: 'doh', name: 'Digital OOH', icon: <FiGlobe />, color: 'success' }
    ];

    // Campaign objectives
    const campaignObjectives = [
        { id: 'awareness', name: 'Brand Awareness', color: 'info' },
        { id: 'traffic', name: 'Traffic Generation', color: 'primary' },
        { id: 'leads', name: 'Lead Generation', color: 'warning' },
        { id: 'sales', name: 'Sales/Conversions', color: 'success' },
        { id: 'installs', name: 'App Installs', color: 'danger' },
        { id: 'retargeting', name: 'Retargeting', color: 'secondary' }
    ];

    // Bidding models
    const biddingModels = [
        { id: 'cpm', name: 'CPM', description: 'Cost per thousand impressions' },
        { id: 'cpc', name: 'CPC', description: 'Cost per click' },
        { id: 'cpa', name: 'CPA', description: 'Cost per action' },
        { id: 'cpv', name: 'CPV', description: 'Cost per view' },
        { id: 'cpl', name: 'CPL', description: 'Cost per lead' }
    ];

    const statusColors = {
        draft: 'secondary',
        active: 'success',
        paused: 'warning',
        completed: 'info',
        archived: 'dark'
    };

    // Load campaigns with useCallback to prevent infinite re-renders
    const loadCampaigns = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current user
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                navigate('/login');
                return;
            }

            // Build query
            let query = supabase
                .from('campaigns')
                .select('*', { count: 'exact' })
                .eq('user_id', user.id);

            // Apply filters
            if (activeTab !== 'all') {
                query = query.eq('status', activeTab);
            }

            if (filterStatus !== 'all') {
                query = query.eq('status', filterStatus);
            }

            if (filterType !== 'all') {
                query = query.eq('campaign_type', filterType);
            }

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase().trim();
                query = query.or(`name.ilike.%${term}%,description.ilike.%${term}%`);
            }

            // Apply sorting
            query = query.order(sortField, { ascending: sortDirection === 'asc' });

            // Apply pagination
            const from = (currentPage - 1) * itemsPerPage;
            const to = from + itemsPerPage - 1;
            query = query.range(from, to);

            const { data, error: fetchError, count } = await query;

            if (fetchError) {
                throw new Error(`Failed to load campaigns: ${fetchError.message}`);
            }

            setCampaigns(data || []);
            setTotalCount(count || 0);
            setTotalPages(Math.ceil((count || 0) / itemsPerPage));

        } catch (err) {
            console.error('Error loading campaigns:', err);
            setError(err.message || 'Failed to load campaigns. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [activeTab, filterStatus, filterType, searchTerm, currentPage, sortField, sortDirection, navigate]);

    // Load ads separately
    const loadAds = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('ads')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;

            setAds(data || []);

        } catch (error) {
            console.error('Error loading ads:', error);
            // Don't set error state for ads as it's secondary data
        }
    }, []);

    useEffect(() => {
        loadCampaigns();
        loadAds();

        // Set up real-time subscription for campaigns
        const channel = supabase
            .channel('campaigns-changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'campaigns',
                },
                () => {
                    loadCampaigns();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [loadCampaigns, loadAds]);

    const handleCreateCampaign = async () => {
        // Validate form
        const errors = {};
        if (!campaignForm.name.trim()) errors.name = 'Campaign name is required';
        if (!campaignForm.budget || campaignForm.budget <= 0) errors.budget = 'Budget must be greater than 0';
        if (campaignForm.start_date && campaignForm.end_date) {
            if (new Date(campaignForm.start_date) > new Date(campaignForm.end_date)) {
                errors.date = 'End date must be after start date';
            }
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                navigate('/login');
                return;
            }

            // Prepare campaign data
            const campaignData = {
                ...campaignForm,
                user_id: user.id,
                budget: parseFloat(campaignForm.budget),
                bid_amount: campaignForm.bid_amount ? parseFloat(campaignForm.bid_amount) : null,
                age_min: parseInt(campaignForm.age_min),
                age_max: parseInt(campaignForm.age_max),
                locations: Array.isArray(campaignForm.locations) ? campaignForm.locations : [campaignForm.locations],
                interests: Array.isArray(campaignForm.interests) ? campaignForm.interests : [],
                devices: Array.isArray(campaignForm.devices) ? campaignForm.devices : ['mobile', 'desktop'],
                languages: Array.isArray(campaignForm.languages) ? campaignForm.languages : ['en'],
                // Initialize metrics
                impressions: 0,
                clicks: 0,
                ctr: 0,
                conversions: 0,
                cpc: 0,
                cpm: 0,
                cpa: 0,
                roas: 0
            };

            const { data, error } = await supabase
                .from('campaigns')
                .insert([campaignData])
                .select()
                .single();

            if (error) throw error;

            // Show success message
            setError(null);
            setCreateModal(false);
            resetForm();
            setFormErrors({});

            // Navigate to the new campaign
            navigate(`/panel/campaigns/${data.id}`);

        } catch (err) {
            console.error('Error creating campaign:', err);
            setError(`Failed to create campaign: ${err.message}`);
        }
    };

    const handleDeleteCampaign = async () => {
        if (!selectedCampaign) return;

        try {
            const { error } = await supabase
                .from('campaigns')
                .delete()
                .eq('id', selectedCampaign.id);

            if (error) throw error;

            // Refresh campaigns list
            await loadCampaigns();
            setDeleteModal(false);
            setSelectedCampaign(null);
            setError(null);

        } catch (err) {
            console.error('Error deleting campaign:', err);
            setError(`Failed to delete campaign: ${err.message}`);
        }
    };

    const handleStatusChange = async (campaignId, newStatus) => {
        try {
            const { error } = await supabase
                .from('campaigns')
                .update({ status: newStatus })
                .eq('id', campaignId);

            if (error) throw error;

            // Update local state immediately for better UX
            setCampaigns(prev => prev.map(campaign =>
                campaign.id === campaignId
                    ? { ...campaign, status: newStatus }
                    : campaign
            ));

        } catch (err) {
            console.error('Error updating campaign status:', err);
            setError(`Failed to update campaign status: ${err.message}`);
        }
    };

    const handleLinkAd = async (campaignId, adId) => {
        try {
            const { error } = await supabase
                .from('ads')
                .update({
                    campaign_id: campaignId,
                    updated_at: new Date().toISOString()
                })
                .eq('id', adId);

            if (error) throw error;

            // Refresh ads data
            await loadAds();
            setLinkAdModal(false);
            setSelectedCampaign(null);
            setError(null);

        } catch (err) {
            console.error('Error linking ad:', err);
            setError(`Failed to link ad: ${err.message}`);
        }
    };

    const handleUnlinkAd = async (adId) => {
        try {
            const { error } = await supabase
                .from('ads')
                .update({
                    campaign_id: null,
                    updated_at: new Date().toISOString()
                })
                .eq('id', adId);

            if (error) throw error;

            await loadAds();

        } catch (err) {
            console.error('Error unlinking ad:', err);
            setError(`Failed to unlink ad: ${err.message}`);
        }
    };

    const resetForm = () => {
        setCampaignForm({
            name: '',
            description: '',
            campaign_type: 'display',
            objective: 'awareness',
            budget_type: 'daily',
            budget: '',
            start_date: format(new Date(), 'yyyy-MM-dd'),
            end_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
            status: 'draft',
            bidding_model: 'cpm',
            bid_amount: '',
            age_min: 18,
            age_max: 65,
            gender: 'all',
            locations: ['US'],
            interests: [],
            devices: ['mobile', 'desktop'],
            languages: ['en']
        });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(parseISO(dateString), 'MMM dd, yyyy');
        } catch (e) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(parseISO(dateString), 'MMM dd, HH:mm');
        } catch (e) {
            return dateString;
        }
    };

    const getAvailableAdsForCampaign = (campaignId) => {
        return ads.filter(ad =>
            !ad.campaign_id || ad.campaign_id === campaignId
        );
    };

    const getCampaignAds = (campaignId) => {
        return ads.filter(ad => ad.campaign_id === campaignId);
    };

    const getCampaignTypeInfo = (type) => {
        return campaignTypes.find(t => t.id === type) || campaignTypes[0];
    };

    const getObjectiveInfo = (objective) => {
        return campaignObjectives.find(o => o.id === objective) || campaignObjectives[0];
    };

    const getCampaignDaysLeft = (campaign) => {
        if (!campaign.end_date || campaign.status !== 'active') return null;
        try {
            const endDate = parseISO(campaign.end_date);
            const today = new Date();
            const daysLeft = differenceInDays(endDate, today);
            return daysLeft > 0 ? daysLeft : 0;
        } catch (e) {
            return null;
        }
    };

    const getBudgetUtilization = (campaign) => {
        if (!campaign.budget || campaign.budget === 0) return 0;
        return Math.min((campaign.spent / campaign.budget) * 100, 100);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Function to truncate text with hover tooltip
    const truncateText = (text, maxLength = 10) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const renderEmptyState = () => {
        const hasFilters = searchTerm || filterStatus !== 'all' || filterType !== 'all' || activeTab !== 'all';

        return (
            <div className="text-center py-5">
                <div className="empty-state-icon mb-4">
                    {hasFilters ? (
                        <FiFilter size={48} className="text-muted" />
                    ) : (
                        <FiTarget size={48} className="text-muted" />
                    )}
                </div>
                <h5 className="fw-semibold mb-2">
                    {hasFilters ? 'No matching campaigns found' : 'No campaigns yet'}
                </h5>
                <p className="text-muted fs-7 mb-4">
                    {hasFilters
                        ? 'Try adjusting your filters or search terms'
                        : 'Create your first campaign to start advertising'
                    }
                </p>
                <div className="d-flex justify-content-center gap-2">
                    {hasFilters ? (
                        <Button
                            color="secondary"
                            onClick={() => {
                                setSearchTerm('');
                                setFilterStatus('all');
                                setFilterType('all');
                                setActiveTab('all');
                            }}
                            size="sm"
                            className="fs-7"
                        >
                            Clear Filters
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            onClick={() => navigate('/panel/NewCampaignWizardPage')}
                            size="sm"
                            className="fs-7"
                        >
                            <FiPlus className="me-1" /> Create Campaign
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    const renderLoadingState = () => (
        <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3 fs-7 text-muted">Loading campaigns...</p>
        </div>
    );

    const renderCampaignRow = (campaign) => {
        const typeInfo = getCampaignTypeInfo(campaign.campaign_type);
        const objectiveInfo = getObjectiveInfo(campaign.objective);
        const campaignAds = getCampaignAds(campaign.id);
        const budgetPercentage = getBudgetUtilization(campaign);
        const daysLeft = getCampaignDaysLeft(campaign);

        return (
            <tr key={campaign.id}>
                <td>
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            {typeInfo.icon}
                        </div>
                        <div>
                            <div
                                className="fw-semibold"
                                title={campaign.name}
                                style={{ cursor: 'help' }}
                            >
                                {truncateText(campaign.name)}
                            </div>
                            <div className="text-muted fs-7">
                                {truncateText(campaign.description || 'No description', 15)}
                            </div>
                        </div>
                    </div>
                </td>
                <td>
                    <Badge color={typeInfo.color} pill className="fs-7">
                        {truncateText(typeInfo.name, 8)}
                    </Badge>
                </td>
                <td>
                    <Badge color={objectiveInfo.color} pill className="fs-7">
                        {truncateText(objectiveInfo.name, 8)}
                    </Badge>
                </td>
                <td>
                    <div className="d-flex flex-column align-items-start">
                        {/* Status Icon Only */}
                        <div className="mb-2">
                            {campaign.status === 'active' && (
                                <div title="Active">
                                    <FiPlay size={18} className="text-success" />
                                </div>
                            )}
                            {campaign.status === 'paused' && (
                                <div title="Paused">
                                    <FiPause size={18} className="text-warning" />
                                </div>
                            )}
                            {campaign.status === 'draft' && (
                                <div title="Draft">
                                    <FiFileText size={18} className="text-secondary" />
                                </div>
                            )}
                            {campaign.status === 'completed' && (
                                <div title="Completed">
                                    <FiCheckCircle size={18} className="text-info" />
                                </div>
                            )}
                            {campaign.status === 'archived' && (
                                <div title="Archived">
                                    <FiArchive size={18} className="text-dark" />
                                </div>
                            )}
                        </div>

                        {/* Quick Action Buttons */}
                        <div className="d-flex align-items-center gap-1">
                            {campaign.status === 'active' && (
                                <Button
                                    size="sm"
                                    color="link"
                                    className="p-0 text-warning"
                                    onClick={() => handleStatusChange(campaign.id, 'paused')}
                                    title="Pause Campaign"
                                >
                                    <FiPause size={14} />
                                </Button>
                            )}

                            {campaign.status === 'paused' && (
                                <Button
                                    size="sm"
                                    color="link"
                                    className="p-0 text-success"
                                    onClick={() => handleStatusChange(campaign.id, 'active')}
                                    title="Resume Campaign"
                                >
                                    <FiPlay size={14} />
                                </Button>
                            )}

                            {campaign.status === 'draft' && (
                                <Button
                                    size="sm"
                                    color="link"
                                    className="p-0 text-success"
                                    onClick={() => handleStatusChange(campaign.id, 'active')}
                                    title="Activate Campaign"
                                >
                                    <FiPlay size={14} />
                                </Button>
                            )}

                            {daysLeft !== null && campaign.status === 'active' && (
                                <div className="text-muted fs-7">
                                    {daysLeft}d
                                </div>
                            )}
                        </div>
                    </div>
                </td>
                <td>
                    <div className="d-flex flex-column">
                        <span className="fw-semibold">{formatCurrency(campaign.spent)}</span>
                        <span className="text-muted fs-7">of {formatCurrency(campaign.budget)}</span>
                        <Progress
                            value={budgetPercentage}
                            className="mt-1"
                            style={{ height: '3px' }}
                            color={budgetPercentage > 80 ? 'warning' : budgetPercentage > 95 ? 'danger' : 'primary'}
                        />
                    </div>
                </td>
                <td>
                    <div className="d-flex flex-column">
                        <span className="fw-semibold">{formatNumber(campaign.impressions || 0)}</span>
                        <span className="text-muted fs-7">{formatNumber(campaign.clicks || 0)} clicks</span>
                    </div>
                </td>
                <td>
                    <div className="d-flex align-items-center">
                        {(campaign.ctr || 0) > 0 ? (
                            <>
                                <span className={`fw-semibold ${campaign.ctr >= 3 ? 'text-success' : campaign.ctr >= 1 ? 'text-warning' : 'text-danger'}`}>
                                    {(campaign.ctr || 0).toFixed(2)}%
                                </span>
                                {campaign.ctr >= 3 ? (
                                    <FiTrendingUp className="ms-1 text-success" size={14} />
                                ) : campaign.ctr >= 1 ? (
                                    <FiTrendingDown className="ms-1 text-warning" size={14} />
                                ) : (
                                    <FiTrendingDown className="ms-1 text-danger" size={14} />
                                )}
                            </>
                        ) : (
                            <span className="text-muted">-</span>
                        )}
                    </div>
                </td>
                <td>
                    <Button
                        color="link"
                        size="sm"
                        className="p-0 text-decoration-none fs-7"
                        onClick={() => navigate(`/panel/campaigns/${campaign.id}/analytics`)}
                    >
                        <FiBarChart2 className="me-1" size={14} />
                        Analytics
                    </Button>
                </td>
                <td>
                    <div className="d-flex align-items-center">
                        <Badge color="light" className="me-2 fs-7">
                            {campaignAds.length} ads
                        </Badge>
                        <Button
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => {
                                setSelectedCampaign(campaign);
                                setLinkAdModal(true);
                            }}
                        >
                            <FiLink size={14} />
                        </Button>
                    </div>
                </td>
                <td>
                    <div className="text-muted fs-7">
                        {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                    </div>
                </td>
                <td>
                    <UncontrolledDropdown>
                        <DropdownToggle color="link" size="sm" className="p-0">
                            <FiMoreVertical />
                        </DropdownToggle>
                        <DropdownMenu left>
                            <DropdownItem onClick={() => navigate(`/panel/campaigns/${campaign.id}`)}>
                                <FiEye className="me-2" /> View Details
                            </DropdownItem>
                            <DropdownItem onClick={() => navigate(`/campaigns/${campaign.id}/analytics`)}>
                                <FiBarChart2 className="me-2" /> View Analytics
                            </DropdownItem>
                            <DropdownItem onClick={() => navigate(`/panel/campaigns/${campaign.id}/edit`)}>
                                <FiEdit className="me-2" /> Edit
                            </DropdownItem>

                            {campaign.status === 'active' && (
                                <DropdownItem onClick={() => handleStatusChange(campaign.id, 'paused')}>
                                    <FiPause className="me-2" /> Pause
                                </DropdownItem>
                            )}

                            {campaign.status === 'paused' && (
                                <DropdownItem onClick={() => handleStatusChange(campaign.id, 'active')}>
                                    <FiPlay className="me-2" /> Resume
                                </DropdownItem>
                            )}

                            {campaign.status === 'draft' && (
                                <DropdownItem onClick={() => handleStatusChange(campaign.id, 'active')}>
                                    <FiPlay className="me-2" /> Activate
                                </DropdownItem>
                            )}

                            {campaign.status === 'active' && (
                                <DropdownItem onClick={() => handleStatusChange(campaign.id, 'completed')}>
                                    <FiArchive className="me-2" /> Complete
                                </DropdownItem>
                            )}

                            <DropdownItem divider />

                            <DropdownItem
                                onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setDeleteModal(true);
                                }}
                                className="text-danger"
                            >
                                <FiTrash2 className="me-2" /> Delete
                            </DropdownItem>
                        </DropdownMenu>
                    </UncontrolledDropdown>
                </td>
            </tr>
        );
    };

    return (
        <div className="">
            {/* Error Alert */}
            {error && (
                <Alert color="danger" className="mb-4 fs-7" toggle={() => setError(null)}>
                    <FiAlertCircle className="me-2" /> {error}
                </Alert>
            )}

            {/* Header */}
            <div className="page-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h1 className="h4 fw-semibold mb-1">Campaigns</h1>
                        <p className="text-muted mb-0 fs-7">
                            {totalCount > 0
                                ? `${totalCount} campaign${totalCount !== 1 ? 's' : ''}`
                                : 'Manage your advertising campaigns'
                            }
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            color="primary"
                            onClick={() => navigate('/panel/NewCampaignWizardPage')}
                            className="d-flex align-items-center fs-7"
                        >
                            <FiPlus className="me-1" size={14} />
                            Create Campaign
                        </Button>
                        <Button
                            outline
                            color="secondary"
                            onClick={loadCampaigns}
                            disabled={loading}
                            className="fs-7"
                        >
                            <FiRefreshCw className={loading ? 'spin' : ''} />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4 w-100">
                <CardBody className="p-3">
                    <Row className="g-3 align-items-center">
                        <Col md={4}>
                            <div className="search-box">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-transparent border-0">
                                        <FiSearch size={14} />
                                    </span>
                                    <Input
                                        type="text"
                                        placeholder="Search campaigns..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="border-0 fs-7"
                                    />
                                    {searchTerm && (
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="text-muted"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <FiXCircle size={14} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Input
                                    type="select"
                                    value={filterStatus}
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="fs-7"
                                >
                                    <option value="all">All Status</option>
                                    <option value="draft">Draft</option>
                                    <option value="active">Active</option>
                                    <option value="paused">Paused</option>
                                    <option value="completed">Completed</option>
                                    <option value="archived">Archived</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Input
                                    type="select"
                                    value={filterType}
                                    onChange={(e) => {
                                        setFilterType(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="fs-7"
                                >
                                    <option value="all">All Types</option>
                                    {campaignTypes.map(type => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={2} className="text-md-end">
                            <Button
                                outline
                                color="secondary"
                                size="sm"
                                className="fs-7"
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterStatus('all');
                                    setFilterType('all');
                                    setActiveTab('all');
                                    setCurrentPage(1);
                                }}
                            >
                                <FiFilter className="me-1" /> Reset
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Status Tabs */}
            <Nav tabs className="mb-4">
                <NavItem>
                    <NavLink
                        className={`fs-7 ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('all');
                            setCurrentPage(1);
                        }}
                    >
                        All <Badge color="light" className="ms-1">{totalCount}</Badge>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`fs-7 ${activeTab === 'active' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('active');
                            setCurrentPage(1);
                        }}
                    >
                        Active <Badge color="light" className="ms-1">
                            {campaigns.filter(c => c.status === 'active').length}
                        </Badge>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`fs-7 ${activeTab === 'paused' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('paused');
                            setCurrentPage(1);
                        }}
                    >
                        Paused
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`fs-7 ${activeTab === 'draft' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('draft');
                            setCurrentPage(1);
                        }}
                    >
                        Draft
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={`fs-7 ${activeTab === 'completed' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('completed');
                            setCurrentPage(1);
                        }}
                    >
                        Completed
                    </NavLink>
                </NavItem>
            </Nav>

            {/* Campaigns Table */}
            <Card className="border-0 shadow-sm">
                <CardBody className="p-0">
                    {loading ? (
                        renderLoadingState()
                    ) : campaigns.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <>
                            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                <Table size='sm' hover className="" responsive>
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                Campaign {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th>Type</th>
                                            <th>Objective</th>
                                            <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th onClick={() => handleSort('spent')} style={{ cursor: 'pointer' }}>
                                                Budget {sortField === 'spent' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th onClick={() => handleSort('impressions')} style={{ cursor: 'pointer' }}>
                                                Impressions {sortField === 'impressions' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th onClick={() => handleSort('ctr')} style={{ cursor: 'pointer' }}>
                                                CTR {sortField === 'ctr' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th>Analytics</th>
                                            <th>Ads</th>
                                            <th>Dates</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map(renderCampaignRow)}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="border-top p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted fs-7">
                                            Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                                            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} campaigns
                                        </div>
                                        <Pagination className="mb-0 pagination-sm">
                                            <PaginationItem disabled={currentPage === 1}>
                                                <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                                            </PaginationItem>

                                            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }

                                                return (
                                                    <PaginationItem key={pageNum} active={currentPage === pageNum}>
                                                        <PaginationLink onClick={() => setCurrentPage(pageNum)}>
                                                            {pageNum}
                                                        </PaginationLink>
                                                    </PaginationItem>
                                                );
                                            })}

                                            <PaginationItem disabled={currentPage === totalPages}>
                                                <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                                            </PaginationItem>
                                        </Pagination>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Create Campaign Modal */}
            <Modal isOpen={createModal} toggle={() => setCreateModal(!createModal)} size="lg">
                <ModalHeader toggle={() => setCreateModal(!createModal)} className="fs-6">
                    Create New Campaign
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Campaign Name *</Label>
                                    <Input
                                        type="text"
                                        value={campaignForm.name}
                                        onChange={(e) => {
                                            setCampaignForm({ ...campaignForm, name: e.target.value });
                                            if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                                        }}
                                        placeholder="Enter campaign name"
                                        className="fs-7"
                                        invalid={!!formErrors.name}
                                    />
                                    {formErrors.name && (
                                        <div className="invalid-feedback d-block fs-7">
                                            {formErrors.name}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Campaign Type</Label>
                                    <Input
                                        type="select"
                                        value={campaignForm.campaign_type}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
                                        className="fs-7"
                                    >
                                        {campaignTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label className="fs-7">Description</Label>
                            <Input
                                type="textarea"
                                value={campaignForm.description}
                                onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                                placeholder="Describe your campaign"
                                className="fs-7"
                                rows="2"
                            />
                        </FormGroup>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Objective</Label>
                                    <Input
                                        type="select"
                                        value={campaignForm.objective}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, objective: e.target.value })}
                                        className="fs-7"
                                    >
                                        {campaignObjectives.map(obj => (
                                            <option key={obj.id} value={obj.id}>
                                                {obj.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Status</Label>
                                    <Input
                                        type="select"
                                        value={campaignForm.status}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, status: e.target.value })}
                                        className="fs-7"
                                    >
                                        <option value="draft">Draft</option>
                                        <option value="active">Active</option>
                                        <option value="paused">Paused</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Budget Type</Label>
                                    <Input
                                        type="select"
                                        value={campaignForm.budget_type}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, budget_type: e.target.value })}
                                        className="fs-7"
                                    >
                                        <option value="daily">Daily Budget</option>
                                        <option value="lifetime">Lifetime Budget</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Budget Amount ($) *</Label>
                                    <Input
                                        type="number"
                                        value={campaignForm.budget}
                                        onChange={(e) => {
                                            setCampaignForm({ ...campaignForm, budget: e.target.value });
                                            if (formErrors.budget) setFormErrors({ ...formErrors, budget: undefined });
                                        }}
                                        placeholder="Enter budget"
                                        className="fs-7"
                                        min="1"
                                        step="0.01"
                                        invalid={!!formErrors.budget}
                                    />
                                    {formErrors.budget && (
                                        <div className="invalid-feedback d-block fs-7">
                                            {formErrors.budget}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Start Date</Label>
                                    <Input
                                        type="date"
                                        value={campaignForm.start_date}
                                        onChange={(e) => {
                                            setCampaignForm({ ...campaignForm, start_date: e.target.value });
                                            if (formErrors.date) setFormErrors({ ...formErrors, date: undefined });
                                        }}
                                        className="fs-7"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">End Date</Label>
                                    <Input
                                        type="date"
                                        value={campaignForm.end_date}
                                        onChange={(e) => {
                                            setCampaignForm({ ...campaignForm, end_date: e.target.value });
                                            if (formErrors.date) setFormErrors({ ...formErrors, date: undefined });
                                        }}
                                        className="fs-7"
                                    />
                                    {formErrors.date && (
                                        <div className="invalid-feedback d-block fs-7">
                                            {formErrors.date}
                                        </div>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Bidding Model</Label>
                                    <Input
                                        type="select"
                                        value={campaignForm.bidding_model}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, bidding_model: e.target.value })}
                                        className="fs-7"
                                    >
                                        {biddingModels.map(model => (
                                            <option key={model.id} value={model.id}>
                                                {model.name} - {model.description}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="fs-7">Bid Amount ($)</Label>
                                    <Input
                                        type="number"
                                        value={campaignForm.bid_amount}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, bid_amount: e.target.value })}
                                        placeholder="Enter bid amount"
                                        className="fs-7"
                                        min="0"
                                        step="0.01"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <Button color="secondary" onClick={() => {
                        setCreateModal(false);
                        resetForm();
                        setFormErrors({});
                    }} className="fs-7">
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCreateCampaign} className="fs-7">
                        Create Campaign
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} size="sm">
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)} className="fs-6">
                    Delete Campaign
                </ModalHeader>
                <ModalBody>
                    <div className="text-center py-3">
                        <FiAlertCircle size={48} className="text-danger mb-3" />
                        <h6 className="fw-semibold mb-2">
                            Delete "{selectedCampaign?.name}"?
                        </h6>
                        <p className="text-muted fs-7 mb-0">
                            This action cannot be undone. All ads and performance data will be removed.
                        </p>
                    </div>
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <Button color="secondary" onClick={() => setDeleteModal(false)} className="fs-7">
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDeleteCampaign} className="fs-7">
                        Delete Campaign
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Link Ad Modal */}
            <Modal isOpen={linkAdModal} toggle={() => setLinkAdModal(!linkAdModal)} size="md">
                <ModalHeader toggle={() => setLinkAdModal(!linkAdModal)} className="fs-6">
                    Link Ads to "{selectedCampaign?.name}"
                </ModalHeader>
                <ModalBody>
                    {ads.length === 0 ? (
                        <div className="text-center py-4">
                            <FiPackage size={32} className="text-muted mb-3" />
                            <p className="text-muted fs-7 mb-3">No ads available to link</p>
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => {
                                    setLinkAdModal(false);
                                    navigate('/panel/ads/new');
                                }}
                                className="fs-7"
                            >
                                Create New Ad
                            </Button>
                        </div>
                    ) : (
                        <div className="ads-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {getAvailableAdsForCampaign(selectedCampaign?.id).map(ad => (
                                <Card key={ad.id} className="mb-2 border-0 shadow-sm">
                                    <CardBody className="p-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold fs-7">{ad.name}</div>
                                                <div className="text-muted fs-7 d-flex align-items-center">
                                                    <Badge color="light" size="sm" className="me-2">
                                                        {ad.ad_type}
                                                    </Badge>
                                                    <Badge color={statusColors[ad.status]} size="sm" className="me-2">
                                                        {ad.status}
                                                    </Badge>
                                                    {ad.format && <span className="me-2">{ad.format}</span>}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1">
                                                {ad.campaign_id === selectedCampaign?.id ? (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            color="success"
                                                            className="fs-7"
                                                            disabled
                                                        >
                                                            <FiCheckCircle className="me-1" /> Linked
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="secondary"
                                                            onClick={() => handleUnlinkAd(ad.id)}
                                                            className="fs-7"
                                                        >
                                                            <FiXCircle />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        color={ad.campaign_id ? 'secondary' : 'primary'}
                                                        onClick={() => handleLinkAd(selectedCampaign?.id, ad.id)}
                                                        className="fs-7"
                                                        disabled={!!ad.campaign_id && ad.campaign_id !== selectedCampaign?.id}
                                                    >
                                                        <FiLink className="me-1" /> Link
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <Button color="secondary" onClick={() => setLinkAdModal(false)} className="fs-7">
                        Close
                    </Button>
                    <Button color="primary" onClick={() => {
                        setLinkAdModal(false);
                        navigate('/panel/ads/new');
                    }} className="fs-7">
                        <FiPlus className="me-1" /> Create New Ad
                    </Button>
                </ModalFooter>
            </Modal>

            <style jsx>{`
                   .campaigns-page {
        padding: 1.5rem;
        min-height: calc(100vh - 120px);
        width : 100% !important
    }
    
    /* Dropdown menu opening to the left */
    .dropdown-menu-end {
        right: 0;
        left: auto !important;
        transform: none !important;
    }
    
    .dropdown-menu {
        position: absolute;
        top: 100%;
        right: 0;
        left: auto !important;
        margin-top: 0.125rem;
        margin-left: 0;
        margin-right: 0;
        transform: none !important;
        min-width: 160px;
        z-index: 1000;
    }
    
    .dropdown-toggle::after {
        display: none;
    }
    
    .table .dropdown-menu {
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        border: 1px solid #e0e0e0;
        border-radius: 6px;
    }
    
    .dropdown-item {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        display: flex;
        align-items: center;
    }
    
   
    
    .dropdown-divider {
        margin: 0.25rem 0;
    }
    
    /* For mobile responsiveness */
    @media (max-width: 768px) {
        .dropdown-menu-end {
            left: 0 !important;
            right: auto !important;
        }
        
        .dropdown-menu {
            position: fixed;
            min-width: 140px;
            margin: 0;
        }
    }
                .page-header {
                    margin-bottom: 2rem;
                }
                
                .search-box .input-group {
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 4px;
                    transition: all 0.2s ease;
                }
                
                .search-box .input-group:focus-within {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
                }
                
                .search-box input:focus {
                    box-shadow: none;
                }
                
                .nav-tabs {
                    border-bottom: 1px solid #e0e0e0;
                }
                
                .nav-tabs .nav-link {
                    border: none;
                    color: #6c757d;
                    padding: 0.75rem 1rem;
                    font-weight: 500;
                    border-bottom: 2px solid transparent;
                    transition: all 0.2s ease;
                }
                
                .nav-tabs .nav-link:hover {
                    color: #0d6efd;
                    border-bottom-color: #dee2e6;
                }
                
                .nav-tabs .nav-link.active {
                    color: #0d6efd;
                    border-bottom: 2px solid #0d6efd;
                    background: transparent;
                }
                
                .table th {
                    font-weight: 600;
                    color: #495057;
                    border-bottom: 1px solid #f0f0f0;
                    padding: 0.75rem;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background: #f8f9fa;
                    white-space: nowrap;
                }
                
                .table td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background-color 0.15s ease;
                    font-size: 0.875rem;
                    white-space: nowrap;
                }
                
                .table-sm th,
                .table-sm td {
                    padding: 0.5rem;
                }
                
                .table tr:last-child td {
                    border-bottom: none;
                }
                
                .table tr:hover td {
                    background-color: #f8f9fa;
                }
                
                .empty-state-icon {
                    color: #adb5bd;
                }
                
                .progress {
                    background-color: #e9ecef;
                    border-radius: 2px;
                }
                
                .progress-bar {
                    border-radius: 2px;
                }
                
                .pagination {
                    margin-bottom: 0;
                }
                
                .pagination .page-link {
                    font-size: 0.8125rem;
                    padding: 0.375rem 0.75rem;
                    border-color: #e0e0e0;
                }
                
                .pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                }
                
                .pagination .page-link:focus {
                    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
                }
                
                .pagination-sm .page-link {
                    font-size: 0.75rem;
                    padding: 0.25rem 0.5rem;
                }
                
                .spin {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .ads-list::-webkit-scrollbar {
                    width: 6px;
                }
                
                .ads-list::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .ads-list::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .ads-list::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                
                /* Status badge styles */
                .badge.bg-secondary {
                    background-color: #6c757d !important;
                }
                
                .badge.bg-success {
                    background-color: #198754 !important;
                }
                
                .badge.bg-warning {
                    background-color: #ffc107 !important;
                    color: #000 !important;
                }
                
                .badge.bg-info {
                    background-color: #0dcaf0 !important;
                }
                
                .badge.bg-dark {
                    background-color: #212529 !important;
                }
                
                /* Responsive styles */
                @media (max-width: 768px) {
                    .campaigns-page {
                        padding: 1rem;
                    }
                    
                    .page-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                    
                    .page-header > div {
                        width: 100%;
                    }
                    
                    .table-responsive {
                        font-size: 0.75rem;
                    }
                    
                    .table th, .table td {
                        padding: 0.5rem;
                    }
                    
                    .nav-tabs {
                        overflow-x: auto;
                        white-space: nowrap;
                        flex-wrap: nowrap;
                        padding-bottom: 1px;
                    }
                    
                    .nav-tabs .nav-link {
                        padding: 0.5rem 0.75rem;
                        font-size: 0.75rem;
                    }
                    
                    .pagination {
                        flex-wrap: wrap;
                        justify-content: center;
                    }
                    
                    .pagination .page-link {
                        padding: 0.25rem 0.5rem;
                        font-size: 0.75rem;
                    }
                }
                
                @media (max-width: 576px) {
                    .campaigns-page {
                        padding: 0.75rem;
                    }
                    
                    .modal-dialog {
                        margin: 0.5rem;
                    }
                }
                
                /* Print styles */
                @media print {
                    .campaigns-page {
                        padding: 0;
                    }
                    
                    .page-header,
                    .nav-tabs,
                    .pagination,
                    .modal,
                    .btn,
                    .dropdown {
                        display: none !important;
                    }
                    
                    .table {
                        font-size: 12px;
                    }
                    
                    .table th,
                    .table td {
                        border: 1px solid #dee2e6;
                    }
                }
            `}</style>
        </div>
    );
};

export default CampaignsPage;